import { ChartOptions, TooltipItem } from "chart.js";
import {
  CHART_AXIS_LABEL_COLOR,
  CHART_AXIS_LABEL_EMPHASIS_COLOR,
} from "../../../theme/colors";
import { BarChartSublabel, TooltipFormat } from "./types";

const FONT = "Axiforma, sans-serif";

export const defaultValueFormatter = (value: number) => (value ?? 0).toString();

interface GetBarChartOptionsParams {
  leftAxisLabel?: string;
  bottomAxisLabel?: string;
  valueAxisFormatter?: (value: number) => string;
  series: { name: string; data: { value: number }[]; color: string }[];
  stacked: boolean;
  horizontal: boolean;
  tooltipFormat?: TooltipFormat;
  valueAxisMax?: number;
  emphasizeZeroLine?: boolean;
  sublabels?: BarChartSublabel[];
}

export function getBarChartOptions({
  leftAxisLabel,
  bottomAxisLabel,
  valueAxisFormatter = defaultValueFormatter,
  series,
  stacked,
  horizontal,
  tooltipFormat,
  valueAxisMax,
  emphasizeZeroLine,
  sublabels,
}: GetBarChartOptionsParams): ChartOptions<"bar"> {
  // A second category axis keeps detail lines pinned to their bar while
  // letting the heading above carry its own weight.
  const showSublabels = !!sublabels && !horizontal;
  const indexAxis = horizontal ? "y" : "x";

  // Use default values if tooltipFormat is not provided
  const effectiveTooltipFormat = tooltipFormat || {
    valueFormatter: defaultValueFormatter,
    showSeriesNames: false,
    showTotal: false,
    showTitle: true,
  };

  return {
    indexAxis,
    animation: { duration: 400, easing: "easeOutQuart" as const },
    transitions: { active: { animation: { duration: 200 } } },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "#000000",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#333333",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        filter: stacked
          ? (tooltipItem: TooltipItem<"bar">) =>
              ((tooltipItem.raw as number) ?? 0) !== 0
          : undefined,
        itemSort: (a, b) => {
          // Reverse the tooltip order if reverseOrder is enabled
          if (effectiveTooltipFormat.reverseOrder) {
            return b.datasetIndex - a.datasetIndex;
          }
          return a.datasetIndex - b.datasetIndex;
        },
        callbacks: {
          title: (tooltipItems: TooltipItem<"bar">[]) => {
            if (effectiveTooltipFormat.showTitle === false) {
              return "";
            }
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            return tooltipItems[0].label;
          },
          beforeBody: (tooltipItems: TooltipItem<"bar">[]) => {
            // Show total at the top if enabled
            if (effectiveTooltipFormat.showTotal && stacked) {
              const total = tooltipItems.reduce((sum, item) => {
                return sum + ((item.raw as number) ?? 0);
              }, 0);
              const formatter =
                effectiveTooltipFormat.totalFormatter ||
                effectiveTooltipFormat.valueFormatter ||
                defaultValueFormatter;
              return [`Total: ${formatter(total)}`];
            }
            return [];
          },
          labelColor: (tooltipItem: TooltipItem<"bar">) => {
            // Return the color for the current dataset
            const bgColor = tooltipItem.dataset.backgroundColor;
            // If backgroundColor is an array, use the color at the current data index
            const color = Array.isArray(bgColor)
              ? bgColor[tooltipItem.dataIndex]
              : bgColor;
            return {
              borderColor: color as string,
              backgroundColor: color as string,
            };
          },
          label: (tooltipItem: TooltipItem<"bar">) => {
            const value = (tooltipItem.raw as number) ?? 0;
            const formatter =
              effectiveTooltipFormat.valueFormatter || defaultValueFormatter;
            const label = tooltipItem.label;
            const dataIndex = tooltipItem.dataIndex;
            const datasetIndex = tooltipItem.datasetIndex;
            const formattedValue = formatter(
              value,
              label,
              dataIndex,
              datasetIndex,
            );

            let labelText = formattedValue;

            // Add percentage if enabled and stacked
            if (effectiveTooltipFormat.showPercentage && stacked) {
              // Calculate total for this data point across all datasets
              const total = tooltipItem.chart.data.datasets.reduce(
                (sum, dataset) => {
                  const dataValue = dataset.data[dataIndex] as number;
                  return sum + (dataValue || 0);
                },
                0,
              );

              if (total > 0) {
                const percentage = ((value / total) * 100).toFixed(1);
                labelText = `${formattedValue} (${percentage}%)`;
              }
            }

            if (effectiveTooltipFormat.showSeriesNames) {
              return `${tooltipItem.dataset.label}: ${labelText}`;
            }
            return labelText;
          },
          afterBody: (tooltipItems: TooltipItem<"bar">[]) => {
            // If reverseOrder is enabled, we need to manually construct the labels
            // in reversed order since Chart.js processes them in dataset order
            if (effectiveTooltipFormat.reverseOrder && stacked) {
              // This is handled by reversing the series data order instead
            }
            return [];
          },
        },
      },
    },
    scales: {
      x: {
        stacked,
        max: horizontal ? valueAxisMax : undefined,
        grid: { display: false },
        ticks: {
          font: {
            family: FONT,
            size: 12,
            weight: showSublabels ? "600" : undefined,
          },
          color: showSublabels
            ? CHART_AXIS_LABEL_EMPHASIS_COLOR
            : CHART_AXIS_LABEL_COLOR,
          callback(value, index, ticks) {
            if (horizontal) {
              return valueAxisFormatter(value as number);
            }
            // For categorical data, get the label from the chart
            const label = this.getLabelForValue(value as number);
            return label;
          },
        },
        title: {
          display: !!bottomAxisLabel && !showSublabels,
          text: bottomAxisLabel,
          font: { family: FONT, size: 12 },
          color: CHART_AXIS_LABEL_COLOR,
        },
      },
      x2: {
        type: "category" as const,
        display: showSublabels,
        labels: (sublabels ?? []).map((l) => (Array.isArray(l) ? l : [l])),
        position: "bottom" as const,
        offset: true,
        grid: { display: false },
        border: { display: false },
        ticks: {
          font: { family: FONT, size: 11 },
          color: CHART_AXIS_LABEL_COLOR,
        },
        title: {
          display: !!bottomAxisLabel && showSublabels,
          text: bottomAxisLabel,
          font: { family: FONT, size: 12 },
          color: CHART_AXIS_LABEL_COLOR,
        },
      },
      y: {
        stacked,
        max: horizontal ? undefined : valueAxisMax,
        grid: {
          display: true,
          color: (context) => {
            if (emphasizeZeroLine && context.tick.value === 0) {
              return "#9CA3AF"; // Darker gray for zero line
            }
            return "#F3F4F6";
          },
        },
        ticks: {
          font: { family: FONT, size: 12 },
          color: CHART_AXIS_LABEL_COLOR,
          // Horizontal bar charts allocate height per category, so there is
          // always room for every label — disable auto-skipping.
          autoSkip: !horizontal,
          callback(value) {
            if (horizontal) {
              // For horizontal bars, y-axis shows category labels
              const label = this.getLabelForValue(value as number);
              return label;
            }
            return valueAxisFormatter(value as number);
          },
        },
        title: {
          display: !!leftAxisLabel,
          text: leftAxisLabel,
          font: { family: FONT, size: 12 },
          color: CHART_AXIS_LABEL_COLOR,
        },
      },
    },
    interaction: {
      // For horizontal bars, use "y" axis mode to match the index axis
      // For vertical bars, use "index" mode for standard behavior
      mode: horizontal ? "y" : "index",
      intersect: false,
    },
  };
}
